import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { FaArrowLeft, FaArrowRight, FaPlus, FaMinus, FaStar } from "react-icons/fa";
import { MdElectricBolt } from "react-icons/md";
import { LiaTimesSolid } from "react-icons/lia";
import { TfiRulerAlt } from "react-icons/tfi";
import "swiper/css";
import "swiper/css/navigation";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { addToCart, getCartCount } from "../../../utils/cartUtils";

const Product1 = ({ data }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [themeColor, setThemeColor] = useState("#ffffff");
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef1 = useRef(null);
  const navigate = useNavigate();

  // quick view states
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedSizePrice, setSelectedSizePrice] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState(null);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [chartImage, setChartImage] = useState(null);
  const [chartImage2, setChartImage2] = useState(null);

  const clearMessage = () => setTimeout(() => setMessage(null), 2000);

  function darkenHexColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) - 255 * percent;
    let g = ((num >> 8) & 0x00ff) - 255 * percent;
    let b = (num & 0x0000ff) - 255 * percent;
    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));
    return `rgb(${r}, ${g}, ${b})`;
  }

  // fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const adminId = sessionStorage.getItem("admin_auto_id");
        const appTypeId = sessionStorage.getItem("app_type_id");
        const accessToken = sessionStorage.getItem("accessToken");

        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/get_new_product_list`,
          {
            admin_auto_id: adminId,
            app_type_id: appTypeId,
            component_auto_id: data._id,
            component_type: "Products",
            limit: 6,
            page_number: 1,
            customer_auto_id: accessToken || adminId,
          }
        );

        if (response.data.status === 1 && Array.isArray(response.data.get_products_lists)) {
          const formatted = response.data.get_products_lists.map((p) => ({
            id: p.product_auto_id,
            name: p.product_name,
            description: p.description,
            price: `₹${p.final_product_price}`,
            originalPrice: p.product_price ? `₹${p.product_price}` : undefined,
            discount: p.offer_percentage ? `${p.offer_percentage}% off` : undefined,
            delivery:
              p.time && p.time_unit
                ? `Delivery in ${p.time} ${p.time_unit}`
                : "Delivery time unavailable",
            images: p.product_images
              ? p.product_images.map(
                  (img) =>
                    `https://grobizapp-n.s3.ap-south-1.amazonaws.com/products_images/${img.product_image}`
                )
              : [],
            size: p.size_list || [],
            get_price_lists: p.get_price_lists || [],
            moq: p.moq || 1,
            user_auto_id: p.user_auto_id,
          }));
          setProducts(formatted);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [data._id]);

  // Theme
  useEffect(() => {
    const storedStyle = sessionStorage.getItem("UIStyle");
    if (storedStyle) {
      setThemeColor(JSON.parse(storedStyle).web_theme_colour);
    }
  }, []);

  if (loading || products.length === 0) return null;

  return (
    <div
      className="product1-component p-4 relative"
      style={{ backgroundColor: data.web_background_color }}
    >
      <div className="mb-4">
        <h1
          className={`text-2xl font-bold ${data.web_title_font} text-${data.title_alignment}`}
          style={{ color: data.web_title_color }}
        >
          {data.title}
        </h1>
      </div>

      {/* Swiper */}
      <Swiper
        ref={swiperRef1}
        modules={[Navigation]}
        spaceBetween={20}
        slidesPerView={1}
        navigation={{
          nextEl: `.custom-next-${data._id}`,
          prevEl: `.custom-prev-${data._id}`,
        }}
        breakpoints={{
          0: { slidesPerView: 1.3 },
          640: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4.5 },
        }}
        onSlideChange={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
        onInit={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
      >
        {products.map((product, index) => (
          <SwiperSlide key={index}>
            <Link
              to={`/product-detail/${product.id}`}
              className="no-underline text-inherit"
            >
              <div className="relative h-full p-1 bg-white border rounded-xl">
                <div className="relative mb-2">
                  <button
                    className="absolute p-2 rounded-lg top-40 right-2 hover:brightness-105"
                    style={{ backgroundColor: themeColor }}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedProduct(product);
                    }}
                  >
                    <FaPlus className="text-gray-700" />
                  </button>
                </div>

                <div className="h-48 mb-4 overflow-hidden rounded-xl">
                  <img
                    src={product.images?.[0] || "https://placehold.co/600x400"}
                    alt={product.name}
                    className="object-cover w-full h-full"
                  />
                </div>

                <h3 className="mb-2 text-lg font-semibold truncate">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-2 min-h-[20px]">
                  {product.description || " "}
                </p>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm sm:text-base md:text-lg font-bold whitespace-nowrap">
                    {product.price}
                  </span>
                  {product.discount && (
                    <>
                      <span className="text-xs text-gray-500 line-through">
                        {product.originalPrice}
                      </span>
                      <span className="text-xs text-red-600 font-semibold">
                        {product.discount}
                      </span>
                    </>
                  )}
                </div>

                <p
                  className="flex items-center text-sm font-medium"
                  style={{ color: themeColor }}
                >
                  <MdElectricBolt className="mr-1 text-yellow-600" />
                  {product.delivery}
                </p>
              </div>
            </Link>
          </SwiperSlide>
        ))}

        {/* Navigation Arrows */}
        <div
          className={`custom-prev-${data._id} absolute top-1/2 left-2 transform -translate-y-1/2 z-10 cursor-pointer bg-white p-2 rounded-full shadow-lg ${
            isBeginning ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <FaArrowLeft className="text-xl text-gray-700" />
        </div>
        <div
          className={`custom-next-${data._id} absolute top-1/2 right-2 transform -translate-y-1/2 z-10 cursor-pointer bg-white p-2 rounded-full shadow-lg ${
            isEnd ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <FaArrowRight className="text-xl text-gray-700" />
        </div>
      </Swiper>

      {/* Quick View Popup */}
      {selectedProduct && (
        <div
          className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex justify-end sm:justify-end items-end sm:items-start transition-all duration-500`}
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="bg-white shadow-lg transform transition-transform duration-700 ease-out
              w-full sm:w-[500px]
              translate-y-0 sm:translate-x-0
              h-[70vh] sm:h-full rounded-t-2xl sm:rounded-none flex flex-col
              mobile-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300">
              <p className="mb-0 text-base sm:text-lg font-bold text-black">
                CHOOSE OPTION
              </p>
              <button
                className="text-lg sm:text-xl text-gray-500"
                onClick={() => setSelectedProduct(null)}
              >
                <LiaTimesSolid />
              </button>
            </div>

            {/* Product content */}
            <div className="overflow-y-auto p-4">
              <Swiper slidesPerView={2.5} spaceBetween={10} className="mt-2 mb-3">
                {selectedProduct.images.map((img, i) => (
                  <SwiperSlide key={i}>
                    <img
                      src={img}
                      alt=""
                      className="object-cover w-full h-60 rounded-lg"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>

              <h2 className="text-lg font-bold">{selectedProduct.name}</h2>
              <p className="text-gray-600 mt-1">{selectedProduct.description}</p>
              <p className="font-semibold mt-1 text-lg">
                Rs. {selectedProduct.price.replace("₹", "")}
              </p>

              <div className="mt-5 flex items-center gap-2">
               {/* Quantity Control */}
                                 <div className="flex items-center">
                                   <button
                                     className="bg-white px-2 py-[12px]  border-l-2 border-t-2 border-b-2 border-gray-200   rounded-l-full"
                                     onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                   >
                                     <FaMinus />
                                   </button>
                                   <input
                                     type="number"
                                     min="1"
                                     value={quantity}
                                     onChange={(e) => {
                                       const value = Math.max(1, parseInt(e.target.value));
                                       setQuantity(value);
                                     }}
                                     className="w-11 text-center border-t-2 border-b-2 border-gray-200   py-[8px]  [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none appearance-none" />
                                   <button
                                     className="bg-white px-2 py-[12px]  border-r-2 border-t-2 border-b-2  border-gray-200 rounded-r-full"
                                     onClick={() => setQuantity(quantity + 1)}
                                   >
                                     <FaPlus />
                                   </button>
                                 </div>
                <button
                  className="flex-1 py-3 rounded-full text-white font-semibold"
                  style={{ backgroundColor: themeColor }}
                >
                  ADD TO CART
                </button>
              </div>

              <div className="text-center mt-4">
                <Link
                  to={`/product-detail/${selectedProduct.id}`}
                  className="underline text-sm"
                >
                  View Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUpSlow {
          0% {
            transform: translateY(100%);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @media (max-width: 768px) {
          .mobile-slide-up {
            animation: slideUpSlow 0.8s ease-out forwards;
          }
        }
      `}</style>
    </div>
  );
};

export default Product1;
